import { CircleCheck, X } from "lucide-react";
import { useState } from "react";
import clsx from "clsx";
import { _ } from "i18n";
import { Button } from "ui-kit-v2/src/button/button";
import { CommonModal } from "ui-kit-v2/src/modal/common-modal";
import { useAttachStore } from "../../../attach-store";
import { useEditStore } from "../../../edit-store";
import { ATTACH_CLIPS_MAX, getAttachTargetKey, hasAttachSelectionChanged } from "../../../attach-mode";
import { AttachModeKind, GamePageResponse } from "../../../types";
import { useCoachReviewsStore } from "../../../stores/coach-reviews.store";
import { useFullscreenModalContainer } from "../../../useFullscreenModalContainer";
import styles from "./styles.module.scss";
import { getClipLabel } from "common/src/gamePage/utils/clips";

export function AttachClipsBanner({ gameData, userId }: { gameData: GamePageResponse; userId: number }) {
	const { target, exitAttachMode, setAttachedEvents, attachedEventsByTargetKey } = useAttachStore();
	const { selectedEvents, setSelectedEvents } = useEditStore();
	const upsertDraft = useCoachReviewsStore((state) => state.upsertDraft);
	const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
	const fullscreenModalContainer = useFullscreenModalContainer();

	if (target === null) {
		return null;
	}

	const attachedEvents = attachedEventsByTargetKey[getAttachTargetKey(target)] ?? [];
	const selectedCount = selectedEvents.length;
	const isMaxReached = selectedCount >= ATTACH_CLIPS_MAX;
	// Clearing every clip is a valid save: it detaches the clips that were attached before
	const isDetachingAllEvents = selectedCount === 0 && attachedEvents.length > 0;
	const hasUnsavedSelectionChanges = hasAttachSelectionChanged(attachedEvents, selectedEvents);
	const attachButtonText = isDetachingAllEvents
		? _("game.attachMode.banner.save")
		: _("game.attachMode.banner.attach", { count: String(selectedCount) });
	const isPlayerTarget = target.kind === AttachModeKind.Player;
	const title = _("game.attachMode.banner.title", { name: target.name });
	const selectedCountLabel = _("game.attachMode.banner.selectedCount", { count: String(selectedCount) });

	const handleRemoveEvent = (eventId: number) => {
		setSelectedEvents((previousSelectedEvents) => previousSelectedEvents.filter((mergedEvent) => mergedEvent.id !== eventId));
	};

	const handleAttach = () => {
		setAttachedEvents(target, selectedEvents);
		const attachedEventIds = selectedEvents.flatMap((mergedEvent) => mergedEvent.eventIds);

		if (target.kind === AttachModeKind.Team) {
			upsertDraft({ kind: "team", userId: userId, gameId: gameData.game.id, teamId: target.teamId, attachedEventIds: attachedEventIds });
		} else {
			upsertDraft({ kind: "player", userId: userId, gameId: gameData.game.id, playerId: target.playerId, attachedEventIds: attachedEventIds });
		}

		exitAttachMode();
	};

	const handleCancelClick = () => {
		if (hasUnsavedSelectionChanges) {
			setShowDiscardConfirm(true);
			return;
		}

		exitAttachMode();
	};

	const handleDiscardConfirm = () => {
		setShowDiscardConfirm(false);
		exitAttachMode();
	};

	const handleDiscardCancel = () => {
		setShowDiscardConfirm(false);
	};

	return (
		<>
			<div className={styles.banner}>
				<div className={styles.banner__panel}>
					<div className={styles.banner__main}>
						<div className={styles.banner__copy}>
							<div className={styles["banner__title-row"]}>
								<CircleCheck className={styles.banner__check} aria-hidden />
								<p className={clsx(styles.banner__title, isPlayerTarget && "sensitive-me")}>{title}</p>
							</div>
							<p className={styles.banner__instruction}>{_("game.attachMode.banner.instruction")}</p>
							{isMaxReached && <p className={styles["banner__max-reached"]}>{_("game.attachMode.banner.maxReached")}</p>}
						</div>
						{selectedCount > 0 && <span className={styles.banner__count}>{selectedCountLabel}</span>}
						<div className={styles.banner__actions}>
							<Button
								type="button"
								variant="neutral"
								text={_("btn.cancel")}
								onClick={handleCancelClick}
								className={styles["banner__button"]}
							/>
							<Button
								type="button"
								variant="primary"
								text={attachButtonText}
								disabled={selectedCount === 0 && isDetachingAllEvents === false}
								onClick={handleAttach}
								className={styles["banner__button"]}
							/>
						</div>
					</div>
					{selectedCount > 0 && (
						<div className={styles.banner__tags}>
							{selectedEvents.map((mergedEvent) => (
								<span key={mergedEvent.id} className={styles.banner__tag}>
									{getClipLabel(mergedEvent)}
									<button
										type="button"
										className={styles["banner__tag-remove"]}
										onClick={() => handleRemoveEvent(mergedEvent.id)}
										aria-label={_("game.attachMode.banner.removeClip")}
									>
										<X aria-hidden />
									</button>
								</span>
							))}
						</div>
					)}
				</div>
			</div>

			<CommonModal
				show={showDiscardConfirm}
				onClose={handleDiscardCancel}
				title={_("game.attachMode.banner.discardConfirm.title")}
				size="sm"
				UNSTABLE_portalContainer={fullscreenModalContainer}
				primaryButton={{
					text: _("game.attachMode.banner.discardConfirm.confirm"),
					onClick: handleDiscardConfirm,
				}}
				secondaryButton={{
					text: _("game.attachMode.banner.discardConfirm.cancel"),
					onClick: handleDiscardCancel,
				}}
			>
				<div>{_("game.attachMode.banner.discardConfirm.body")}</div>
			</CommonModal>
		</>
	);
}
